import { httpClient } from "../http-client";
import { NEW_API_ENDPOINTS } from "shared/constants/api";
import type { ApiResponse, Pageable } from "shared/types/api";
import type { PageResponse } from "shared/types/api";
import type { WithdrawalPendingUser } from "domain/types/withdrawal";
import type {
  AdminGeneralUserPage,
  AdminGeneralUserSummary,
  AdminManagerUserPage,
  ManagerRole,
  RequestStatus,
} from "domain/types/admin-user";

/**
 * 어드민 "사용자 관리" 화면 (Users.tsx) 전용 API 서비스.
 *
 * 거래 업체 관리자(COMPANY_ADMIN / COMPANY_MANAGER) 목록 + (POD24) 일반 사용자(USER) 목록 +
 * 일반 사용자 탭 상단 요약 카드 4종을 호출한다.
 *
 * 무한 스크롤 + 가나다순 정렬은 백엔드 default 에 위임 (회사 목록과 동일 패턴).
 */
export interface ListManagerUsersParams extends Pageable {
  keyword?: string | null;
  role?: ManagerRole | null;
  companyId?: number | null;
}

export interface ListWithdrawalPendingParams extends Pageable {
  keyword?: string | null;
}

export interface ListGeneralUsersParams extends Pageable {
  keyword?: string | null;
  requestStatus?: RequestStatus | null;
  joinedWithinDays?: number | null;
}

function buildQuery(params: Record<string, unknown>): string {
  const usp = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    if (Array.isArray(value)) {
      value.forEach((v) => usp.append(key, String(v)));
    } else {
      usp.append(key, String(value));
    }
  });
  const q = usp.toString();
  return q ? `?${q}` : "";
}

export class AdminUserApiService {
  /** 거래 업체 관리자 목록 (COMPANY_ADMIN / COMPANY_MANAGER). 백엔드 default 정렬: companyName ASC, name ASC. */
  async listManagers(
    params: ListManagerUsersParams,
  ): Promise<ApiResponse<AdminManagerUserPage>> {
    const url = `${NEW_API_ENDPOINTS.adminController.GET.managerUsers}${buildQuery(params as Record<string, unknown>)}`;
    const response = await httpClient.get<ApiResponse<AdminManagerUserPage>>(url);

    if (response.data.errorCode) {
      throw new Error(
        response.data.errorMsg ||
          "거래 업체 관리자 목록을 가져오지 못했습니다.",
      );
    }
    return response.data;
  }

  /** (POD24) 일반 사용자 목록 (role = USER). 백엔드 default 정렬: name ASC. */
  async listGeneralUsers(
    params: ListGeneralUsersParams,
  ): Promise<ApiResponse<AdminGeneralUserPage>> {
    const url = `${NEW_API_ENDPOINTS.adminController.GET.generalUsers}${buildQuery(params as Record<string, unknown>)}`;
    const response = await httpClient.get<ApiResponse<AdminGeneralUserPage>>(url);

    if (response.data.errorCode) {
      throw new Error(
        response.data.errorMsg || "일반 사용자 목록을 가져오지 못했습니다.",
      );
    }
    return response.data;
  }

  /** 일반 사용자 탭 상단 요약 카드 4종. */
  async getGeneralUserSummary(): Promise<
    ApiResponse<AdminGeneralUserSummary>
  > {
    const response = await httpClient.get<ApiResponse<AdminGeneralUserSummary>>(
      NEW_API_ENDPOINTS.adminController.GET.generalUserSummary,
    );

    if (response.data.errorCode) {
      throw new Error(
        response.data.errorMsg ||
          "일반 사용자 요약 정보를 가져오지 못했습니다.",
      );
    }
    return response.data;
  }

  /**
   * 탈퇴 유예중인 회원 목록.
   * 삭제가 임박한 회원이 위로 오도록 백엔드 default 정렬은 신청일 오름차순이다.
   */
  async listWithdrawalPendingUsers(
    params: ListWithdrawalPendingParams,
  ): Promise<ApiResponse<PageResponse<WithdrawalPendingUser>>> {
    const url = `${NEW_API_ENDPOINTS.adminController.GET.withdrawalPendingUsers}${buildQuery(params as Record<string, unknown>)}`;
    const response =
      await httpClient.get<ApiResponse<PageResponse<WithdrawalPendingUser>>>(url);

    if (response.data.errorCode) {
      throw new Error(
        response.data.errorMsg || "탈퇴 유예중 회원을 가져오지 못했습니다.",
      );
    }
    return response.data;
  }

  /** 유예중 회원 복구. 삭제가 끝난 뒤에는 되돌릴 수 없다. */
  async recoverUser(userId: number): Promise<void> {
    const response = await httpClient.put<ApiResponse<boolean>>(
      `/api/admin/user/${userId}/recover`,
      {},
    );

    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "복구에 실패했습니다.");
    }
  }
}

export const adminUserApiService = new AdminUserApiService();
